import {
  IApiContractorGroup,
  IRawContractorGroup,
  TApiContractorGroupsResponse,
  TApiCreateContractorGroupRequest,
  TRawContractorGroup,
} from "@propely/propely-typings";
import { queryClient } from "@src/index";
import { arrayRemove, arrayUnion, collection, doc, getDoc, getDocs, getFirestore, query, setDoc, updateDoc, where } from "firebase/firestore";
import { useQuery } from "react-query";
import { usePropertyKey } from "../property/property.service";
import { propelyApi } from "@src/libs/firebase/api";
import { z } from "zod";

// Method to get a contractor group fetch key
export const useContractorGroupByIDKey = (id: string | undefined | null) => ["contractorGroups-raw", { id }];

// Method to fetch a contractor group
const fetchContractorGroup = async (id: string | undefined | null): Promise<IRawContractorGroup | undefined> => {
  if (!id) {
    return undefined;
  }
  const contractorGroup = await getDoc(doc(getFirestore(), "contractorGroups", id));
  return TRawContractorGroup.parse({
    id: contractorGroup.id,
    ...contractorGroup.data(),
  });
};

const fetchContractorGroups = async (ids: string[]): Promise<IRawContractorGroup[]> => {
  const promises = ids.map((id) => fetchContractorGroup(id));
  const results = await Promise.allSettled(promises);

  results.forEach((result, index) => {
    if (result.status === "fulfilled") {
      const contractorGroup = result.value;
      const id = ids[index];

      queryClient.setQueryData(useContractorGroupByIDKey(id), contractorGroup);
    }
  });

  return results
    .filter((result): result is PromiseFulfilledResult<IRawContractorGroup> => result.status === "fulfilled")
    .map((result) => result.value);
};

export const useContractorGroupByID = (id: string | undefined | null) => {
  const $contractorGroup = useQuery(useContractorGroupByIDKey(id), () => fetchContractorGroup(id));
  return {
    $contractorGroup,
    contractorGroup: $contractorGroup.data,
  };
};

export const useContractorGroupsByIDs = (ids: string[]) => {
  const $contractorGroups = useQuery(["contractorGroups", ids], () => fetchContractorGroups(ids));
  return {
    $contractorGroups,
    contractorGroups: $contractorGroups.data ?? [],
    isLoading: $contractorGroups.isLoading,
  };
};

export const fetchAllContractorGroups = async (): Promise<IApiContractorGroup[]> => {
  const response = await propelyApi().get("contractorGroups");
  const parsedResponse = TApiContractorGroupsResponse.safeParse(response.data);
  if (!parsedResponse.success) {
    throw new Error("Invalid response data");
  }
  return parsedResponse.data.contractorGroups;
};

export const useAllContractorGroupsKey = () => ["api-contractorGroups"];

export const useAllContractorGroups = ({ enabled }: { enabled?: boolean } = {}) => {
  const $contractorGroups = useQuery(useAllContractorGroupsKey(), fetchAllContractorGroups, { enabled });

  return {
    $contractorGroups,
    contractorGroups: $contractorGroups.data ?? [],
  };
};

export const useContractorGroupsInProperty = (propertyID: string) => {
  const $contractorGroups = useQuery(["contractorGroups-property", { propertyID }], async () => {
    const contractorGroups = await getDocs(query(collection(getFirestore(), "contractorGroups"), where("properties", "array-contains", propertyID)));

    const result: IRawContractorGroup[] = [];
    for (const contractorGroup of contractorGroups.docs) {
      const parsedData = TRawContractorGroup.safeParse({
        id: contractorGroup.id,
        ...contractorGroup.data(),
      });
      if (parsedData.success) {
        result.push(parsedData.data);
      }
    }
    return result;
  });

  return {
    $contractorGroups,
    contractorGroups: $contractorGroups.data,
  };
};

export const createContractorGroup = async (data: Omit<IRawContractorGroup, "id">): Promise<string> => {
  const response = await propelyApi().post("contractorGroups", data);

  const parsedResponse = z.object({ id: z.string() }).safeParse(response.data); //TApiCreateContractorGroupReponse
  if (!parsedResponse.success) {
    throw new Error("Invalid response data");
  }

  // Invalidate queries after the document is successfully created
  await Promise.all(
    data.properties.map(async (propertyID) => {
      await queryClient.invalidateQueries(usePropertyKey(propertyID));
      await queryClient.invalidateQueries("api-contractorGroups");
      await queryClient.invalidateQueries("contractorGroups");
      await queryClient.invalidateQueries("contractors");
      await queryClient.invalidateQueries("contractors-property");
    })
  );

  return parsedResponse.data.id;
};

export const updateContractorGroup = async (id: string, data: Partial<Omit<IRawContractorGroup, "properties">>, userID: string): Promise<void> => {
  const db = getFirestore();
  const contractorGroupRef = doc(db, "contractorGroups", id);

  try {
    await setDoc(
      contractorGroupRef,
      {
        ...data,
        updatedBy: {
          userID: userID,
          date: new Date(),
        },
      },
      { merge: true }
    );

    await queryClient.invalidateQueries(useContractorGroupByIDKey(data.id));
    await queryClient.invalidateQueries("api-contractorGroups");
    await queryClient.invalidateQueries("contractorGroups");
    await queryClient.invalidateQueries("contractors");
    await queryClient.invalidateQueries("contractors-property");
  } catch (error) {
    console.error("Error updating document:", error);
    throw error;
  }
};

export const updatePropertiesInContractor = async (
  contractorID: string,
  properties: string[],
  method: "add" | "remove",
  userID: string
): Promise<void> => {
  const db = getFirestore();
  const propertyPromises: Promise<any>[] = [];
  const successfullProperties: string[] = [];

  for (const property of properties) {
    propertyPromises.push(
      updateDoc(doc(db, "properties", property), {
        contractorGroups: method === "add" ? arrayUnion(contractorID) : arrayRemove(contractorID),
        updatedBy: {
          userID: userID,
          date: new Date(),
        },
      })
        .then(() => successfullProperties.push(property))
        .catch((error) => console.error("Error updating document:", error))
    );
  }

  await Promise.allSettled(propertyPromises);

  await updateDoc(doc(db, "contractorGroups", contractorID), {
    properties: method === "add" ? arrayUnion(...successfullProperties) : arrayRemove(...successfullProperties),
    updatedBy: {
      userID: userID,
      date: new Date(),
    },
  });
  await Promise.all([queryClient.invalidateQueries(useContractorGroupByIDKey(contractorID)), ...invalidatePropertyQueries(properties)]);
  await queryClient.invalidateQueries("api-contractorGroups");
  await queryClient.invalidateQueries("contractorGroups");
  await queryClient.invalidateQueries("contractors");
  await queryClient.invalidateQueries("contractors-property");
};

export const deleteContractorGroup = async (contractor: IRawContractorGroup): Promise<void> => {
  return propelyApi()
    .delete(`contractorGroups/${contractor.id}`)
    .then(async () => {
      await Promise.all([
        queryClient.invalidateQueries(useContractorGroupByIDKey(contractor.id)),
        ...invalidatePropertyQueries(contractor.properties),
      ]);
      await queryClient.invalidateQueries("api-contractorGroups");
      await queryClient.invalidateQueries("contractorGroups");
      await queryClient.invalidateQueries("contractors");
      await queryClient.invalidateQueries("contractors-property");
    });
};

function invalidatePropertyQueries(propertyIDs: string[]): Promise<void>[] {
  return propertyIDs.map((propertyID) => queryClient.invalidateQueries(usePropertyKey(propertyID)));
}

export const prefetchAllContractorGroups = async () => {
  const contractorGroups = await fetchAllContractorGroups();
  queryClient.setQueryData(useAllContractorGroupsKey(), contractorGroups);
};

export const getAllContractorGroupsFromCache = (): IApiContractorGroup[] => {
  const cache = queryClient.getQueryData<IApiContractorGroup[]>(useAllContractorGroupsKey());
  if (!cache) {
    return [];
  }
  return cache;
};
